import {
	Button,
	ConfigProvider,
	Form,
	Grid,
	Input,
	Space,
	Typography,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import JnjLogo from '../../assets/icons/JnJ_MedTech.png';
import { ReactComponent as BrandingLogo } from '../../assets/icons/smart-logo.svg';
import PasswordReset from '../../services/PasswordReset';

const { useBreakpoint } = Grid;
const { Text } = Typography;

const RED = '#ca001b';

function meetsPolicy(value) {
	if (!value) return false;
	const lengthOk = value.length >= 10;

	const hasUpper = /[A-Z]/.test(value);
	const hasLower = /[a-z]/.test(value);
	const hasDigit = /\d/.test(value);
	// Common specials; extend if needed
	const hasSpecial = /[!@#$%^&*()\-_=+[{\]}\\|;:'",.<>/?`~]/.test(value);

	const categoriesCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(
		Boolean,
	).length;

	return lengthOk && categoriesCount >= 3;
}

function CreateNewPassword() {
	const [form] = Form.useForm();
	const screens = useBreakpoint();
	const [submitting, setSubmitting] = useState(false);
	const [response, setResponse] = useState('');
	const [errorRes, setErrorRes] = useState('');

	const PasswordResetService = new PasswordReset();

	const isMobile = !screens.md;

	const [creds, setCreds] = useState({ userid: '', email: '', token: '' });

	useEffect(() => {
		const sp = new URLSearchParams(window.location.search);
		const userid = sp.get('userid') ?? '';
		const email = sp.get('email') ?? '';
		const token = sp.get('token') ?? '';
		setCreds({ userid, email, token });
	}, []);

	const containerStyle = useMemo(
		() => ({
			maxWidth: 480,
			margin: '0 auto',
			padding: isMobile ? 16 : 24,
			background: '#fff',
		}),
		[isMobile],
	);

	const noteBoxStyle = {
		background: '#f5f5f5',
		borderRadius: 6,
		padding: 12,
		color: '#595959',
	};

	const onFinish = async values => {
		try {
			setSubmitting(true);
			setErrorRes('');
			const request = {
				uuid: creds.userid,
				token: creds.token,
				password: btoa(values.newPassword),
			};
			const resetResponse =
				await PasswordResetService.postResetPassword(request);
			setResponse(resetResponse);
		} catch (error) {
			setResponse('');
			setErrorRes(error);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="flex justify-center items-center flex-col pt-20">
			<span className="h-20">
				<BrandingLogo />
			</span>
			<ConfigProvider
				theme={{
					token: {
						colorPrimary: RED,
						colorError: RED, // ensures red required asterisk
						borderRadius: 6,
						fontSize: 14,
					},
					components: {
						Button: {
							controlHeight: 44,
							controlHeightLG: 48,
						},
					},
				}}>
				<div style={containerStyle}>
					<span className="flex flex-col mb-5">
						<div className="font-bold mb-2">Create new password for</div>
						<div className="font-bold">Email : {creds?.email}</div>
						{response && (
							<div className="font-bold text-jnj_active">
								Your password has been updated successfully
							</div>
						)}
						{errorRes && (
							<div className="font-bold text-jnj_red">
								An error occurred while updating your password. Please try again
								and check whether the password meets all criteria. If the
								problem persists, contact support
							</div>
						)}
					</span>
					<Form
						form={form}
						layout="vertical"
						onFinish={onFinish}
						requiredMark
						validateTrigger={['onBlur', 'onChange']}>
						<Form.Item
							label={
								<Text strong style={{ color: '#262626' }}>
									New Password
								</Text>
							}
							name="newPassword"
							rules={[
								{ required: true, message: 'Please enter a new password.' },
								{
									validator: async (_, value) => {
										if (!value) return Promise.resolve();
										if (meetsPolicy(value)) return Promise.resolve();
										return Promise.reject(
											new Error(
												'Must be at least 10 characters and include any 3 of the 4 categories.',
											),
										);
									},
								},
							]}
							hasFeedback>
							<Input.Password
								placeholder="Enter new password"
								size={isMobile ? 'large' : 'middle'}
								autoComplete="new-password"
							/>
						</Form.Item>

						<Form.Item
							label={
								<Text strong style={{ color: '#262626' }}>
									Confirm New Password
								</Text>
							}
							name="confirmPassword"
							dependencies={['newPassword']}
							hasFeedback
							rules={[
								{
									required: true,
									message: 'Please confirm your new password.',
								},
								({ getFieldValue }) => ({
									validator: (_, value) => {
										if (!value) return Promise.resolve();
										if (getFieldValue('newPassword') === value)
											return Promise.resolve();
										return Promise.reject(new Error('Passwords do not match.'));
									},
								}),
							]}>
							<Input.Password
								placeholder="Re-enter new password"
								size={isMobile ? 'large' : 'middle'}
								autoComplete="new-password"
							/>
						</Form.Item>

						<Space direction="vertical" style={{ width: '100%' }} size={12}>
							<div style={noteBoxStyle}>
								<Text>
									New password must be 10 or more characters and contain 3 of
									the following:
								</Text>
								<ul style={{ margin: '8px 0 0 16px' }}>
									<li>Uppercase characters</li>
									<li>Lowercase characters</li>
									<li>Digits (0–9)</li>
									<li>Special characters (!@#$ etc)</li>
								</ul>
							</div>

							<Form.Item shouldUpdate noStyle>
								{() => (
									<Button
										type="primary"
										size="large"
										block
										htmlType="submit"
										style={{
											background: RED,
											borderColor: RED,
											fontWeight: 600,
											color: '#fff',
										}}
										loading={submitting}
										disabled={
											// Disable until fields are filled and valid
											!form.isFieldsTouched(true) ||
											form
												.getFieldsError()
												.some(({ errors }) => errors.length > 0)
										}>
										Create New Password
									</Button>
								)}
							</Form.Item>
						</Space>
					</Form>
				</div>
			</ConfigProvider>
			<span className="flex w-80 h-20">
				<img alt="JnjLogo" src={JnjLogo} />
			</span>
		</div>
	);
}

export default CreateNewPassword;
